<ng-container *ngIf="serverError">
  <div class="modal-header">
    <h4 class="modal-title">Server Error: {{ serverError }}</h4>
    <button type="button" class="close" aria-label="Close" (click)="activeModal.dismiss()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
</ng-container>

<ng-container *ngIf="!serverError">

  <div class="modal-header">
    <h4 class="modal-title">Duplicate Occurrence</h4>
    <button type="button" class="close" aria-label="Close" (click)="activeModal.dismiss()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">

    <p>
      <b>Duplicated Occurrence Info</b> <br>
      <ng-container *ngIf="occurrence.occurrence_name">
        Staff Name: {{ occurrence.occurrence_name }} <br>
      </ng-container>
      <ng-container *ngIf="occurrence.customer_facing_name">
        Customer-Facing Name: {{ occurrence.customer_facing_name }} <br>
      </ng-container>
      Number of Dates: {{ occurrence.event_parts.length }} <br>
      <ng-container *ngIf="occurrence.event_parts.length > 1">
        Start Date: {{ dateStringFromISO8601(occurrence.event_parts[0].start_date) }} <br>
        End Date: {{ dateStringFromISO8601(occurrence.event_parts[occurrence.event_parts.length - 1].end_datetime_isoformat) }}
      </ng-container>
      <ng-container *ngIf="occurrence.event_parts.length === 1">
        Date: {{ dateStringFromISO8601(occurrence.event_parts[0].start_date) }}
      </ng-container>
    </p>

    <hr>

    <div class="form-group">
      <label for="locationId"><b>Location</b></label>
      <select class="custom-select"
              name="locationId" id="locationId"
              [(ngModel)]="locationId">
        <option [ngValue]="null" disabled class="disabled">
          {{ locationService.getLocations() === null ? 'Loading...' : 'Select a Location' }}
        </option>
        <ng-container *ngIf="locationService.getLocations() !== null">
          <option *ngFor="let location of locationService.getLocations()"
                  [ngValue]="location.id">
            {{ location.name }}
          </option>
        </ng-container>
      </select>
    </div>

    <div class="form-group">
      <label for="repeatType"><b>Repeat Type</b></label>
      <select class="custom-select"
              name="repeatType" id="repeatType"
              [(ngModel)]="repeatType">
        <option [ngValue]="RepeatType.selectDates">
          Select Dates
        </option>
        <option [ngValue]="RepeatType.interval">
          Interval
        </option>
      </select>
    </div>

    <div class="alert alert-info" *ngIf="repeatType === RepeatType.selectDates">
      Select one or more start dates for duplicate occurrences. A new occurrence will be created for each start date.
      <ng-container *ngIf="occurrence.event_parts.length > 1">
        <br>
        Because the original occurrence is multi-part, each duplicate occurrence will have the same number of parts, using the time intervals from the original occurrence.
      </ng-container>
    </div>
    <div class="alert alert-info" *ngIf="repeatType === RepeatType.interval">
      Select a date range and repeat interval over which to duplicate occurrences.
      <ng-container *ngIf="occurrence.event_parts.length > 1">
        <br>
        Because the original occurrence is multi-part, each duplicate occurrence will have the same number of parts, using the time intervals from the original occurrence.
      </ng-container>
    </div>
    <div class="alert alert-info" *ngIf="repeatType === RepeatType.monthly">
      Select a date range and days of the week over which to duplicate occurrences. An occurrence will be created for each calendar month in the date range, with each occurrence comprising all of the selected days of the week within the month.
    </div>

    <ng-container *ngIf="repeatType === RepeatType.selectDates">
      <div class="form-group">
        <label><b>Start Date(s)</b></label>
        <ng-container *ngFor="let duplicateStartDate of duplicateStartDates; let i = index">
          <div class="row mb-2">
            <div class="col-8">
              <app-date-picker [id]="'duplicateStartDate' + i.toString() + 'Input'"
                               [(content)]="duplicateStartDate.date"
                               [disabled]="loading"></app-date-picker>
            </div>
            <div class="col-4 pl-0">
              <button class="btn btn-danger" type="button"
                      id="duplicateStartDate{{ i }}Button"
                      [disabled]="loading"
                      [class.disabled]="loading"
                      (click)="removeDuplicateStartDate(i)">
                Remove
              </button>
            </div>
          </div>
          <p *ngIf="duplicateStartDate.errorMessage !== ''" class="text-danger">
            {{ duplicateStartDate.errorMessage }}
          </p>
        </ng-container>
        <button class="btn btn-primary"
                [disabled]="loading || (duplicateStartDates.length >= 20)"
                [class.disabled]="loading || (duplicateStartDates.length >= 20)"
                (click)="duplicateStartDates.push({date: '', errorMessage: ''})">
          Add Start Date
        </button>
      </div>
    </ng-container>

    <!--suppress TypeScriptValidateTypes -->
    <ng-container *ngIf="[RepeatType.interval, RepeatType.monthly].includes(repeatType)">
      <div class="form-group">
        <label><b>First Start Date</b></label>
        <app-date-picker [id]="'firstStartDate'"
                         [(content)]="firstStartDate"></app-date-picker>
      </div>
      <div class="form-group">
        <label><b>Last Start Date</b></label>
        <app-date-picker [id]="'lastStartDate'"
                         [(content)]="lastStartDate"></app-date-picker>
        <p class="form-text text-danger">{{ getStartDateRangeErrorMessage() }}</p>
      </div>
    </ng-container>

    <ng-container *ngIf="repeatType === RepeatType.interval">
      <div class="form-group">
        <label for="interval"><b>Interval</b></label>
        <select class="custom-select"
                name="interval" id="interval"
                [(ngModel)]="interval">
          <option [ngValue]="Interval.weekly">
            Weekly
          </option>
          <option [ngValue]="Interval.monthly">
            Monthly
          </option>
        </select>
        <small class="form-text text-muted">
          The "Monthly" option allow the occurrences to be created starting on certain weekdays of the month. For example, if the selected first start date is the second Wednesday of the month, and the repetition is set to "Monthly", new occurrences will start on the second Wednesday of each month until the selected last start date is reached.
        </small>
      </div>
    </ng-container>

    <ng-container *ngIf="repeatType === RepeatType.monthly">
      <div class="form-check">
        <input class="form-check-input" type="checkbox"
               id="includeSunday" name="includeSunday"
               [(ngModel)]="includeSunday">
        <label class="form-check-label" for="includeSunday">
          Include Sundays
        </label>
      </div>
      <div class="form-check">
        <input class="form-check-input" type="checkbox"
               id="includeMonday" name="includeMonday"
               [(ngModel)]="includeMonday">
        <label class="form-check-label" for="includeMonday">
          Include Mondays
        </label>
      </div>
      <div class="form-check">
        <input class="form-check-input" type="checkbox"
               id="includeTuesday" name="includeTuesday"
               [(ngModel)]="includeTuesday">
        <label class="form-check-label" for="includeTuesday">
          Include Tuesdays
        </label>
      </div>
      <div class="form-check">
        <input class="form-check-input" type="checkbox"
               id="includeWednesday" name="includeWednesday"
               [(ngModel)]="includeWednesday">
        <label class="form-check-label" for="includeWednesday">
          Include Wednesdays
        </label>
      </div>
      <div class="form-check">
        <input class="form-check-input" type="checkbox"
               id="includeThursday" name="includeThursday"
               [(ngModel)]="includeThursday">
        <label class="form-check-label" for="includeThursday">
          Include Thursday
        </label>
      </div>
      <div class="form-check">
        <input class="form-check-input" type="checkbox"
               id="includeFriday" name="includeFriday"
               [(ngModel)]="includeFriday">
        <label class="form-check-label" for="includeFriday">
          Include Fridays
        </label>
      </div>
      <div class="form-check">
        <input class="form-check-input" type="checkbox"
               id="includeSaturday" name="includeSaturday"
               [(ngModel)]="includeSaturday">
        <label class="form-check-label" for="includeSaturday">
          Include Saturdays
        </label>
      </div>
    </ng-container>

    <div *ngIf="errorMessage !== ''" class="alert alert-danger">
      {{ errorMessage }}
    </div>

    <div *ngIf="successMessage !== ''" class="alert alert-danger">
      {{ successMessage }}
    </div>

  </div>

  <div class="modal-footer">
    <button type="button" class="btn btn-secondary"
            (click)="activeModal.close()">
      Go Back
    </button>
    <button type="button" class="btn btn-primary"
            [disabled]="loading || hasInputErrors()"
            [class.disabled]="loading || hasInputErrors()"
            (click)="onSubmit()">
      {{ loading ? 'Loading...' : 'Submit' }}
    </button>
  </div>

</ng-container>
